import React, { useEffect, useState } from "react";
import { Smartphone, X, Download, Plus } from "lucide-react";

const DISMISS_KEY = "bud_install_banner_dismissed_at";
const STANDALONE =
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true);

/**
 * Non-intrusive install banner.
 * - Android/desktop Chromium: shows after the browser fires `beforeinstallprompt`.
 * - iOS Safari: shows our own banner with Add-to-Home-Screen instructions
 *   (Safari never fires beforeinstallprompt).
 * Dismissals are remembered for 14 days.
 */
export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (STANDALONE) return; // already installed, do nothing
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (ts && Date.now() - ts < 14 * 24 * 60 * 60 * 1000) return;

    const ua = window.navigator.userAgent || "";
    const isAppleMobile =
      /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
    if (isAppleMobile && !STANDALONE) {
      setIsIOS(true);
      // delay a few seconds so we don't fight with the age gate
      const t = setTimeout(() => setVisible(true), 5000);
      return () => clearTimeout(t);
    }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {
      /* user dismissed */
    }
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[92%] max-w-md
                 bg-card/95 backdrop-blur border border-border rounded-2xl
                 shadow-[0_18px_50px_-12px_rgba(0,0,0,0.6)] p-4
                 flex items-start gap-3 animate-in slide-in-from-bottom-2 duration-300"
    >
      <div className="w-9 h-9 rounded-full bg-primary/15 text-primary inline-flex items-center justify-center shrink-0">
        <Smartphone className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">Install Bud Link</div>
        <div className="text-xs text-foreground/70 mt-0.5 leading-relaxed">
          {isIOS ? (
            <>
              Tap the <Share className="inline w-3.5 h-3.5 align-text-bottom" /> share
              icon, then <Plus className="inline w-3 h-3 align-text-bottom" /> "Add
              to Home Screen" for one-tap access.
            </>
          ) : (
            "Faster launches, works offline, and never another browser tab."
          )}
        </div>
        {!isIOS && (
          <button
            data-testid="install-prompt-accept"
            onClick={install}
            className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-medium
                       rounded-full bg-primary text-primary-foreground px-4 h-8"
          >
            <Download className="w-3 h-3" /> Install
          </button>
        )}
      </div>
      <button
        data-testid="install-prompt-dismiss"
        onClick={dismiss}
        aria-label="Dismiss"
        className="rounded-full w-7 h-7 inline-flex items-center justify-center
                   text-muted-foreground hover:text-foreground hover:bg-secondary/60"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

// inline share icon for iOS instructions (so we don't pull an extra lucide weight)
const Share = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
);
